// Standard cookware dimensions. Sources:
//   baking dishes — https://www.joyofbaking.com/PanSizes.html
//   sheet pans    — https://en.wikipedia.org/wiki/Sheet_pan
//   skillets      — Lodge cast iron line (6, 8, 10, 12 in diameters)
//   pots          — common saucepan/stockpot capacities in US quarts
// Rectangular dimensions in inches, short side first (matches "9×13" naming).

import { convertUnit, formatQty } from './qty';
import type { UnitSystem, Vessel } from './types';

const STANDARDS: Record<Vessel['kind'], Vessel[]> = {
  'baking-dish': [
    { kind: 'baking-dish', width: 8, height: 8 },
    { kind: 'baking-dish', width: 9, height: 9 },
    { kind: 'baking-dish', width: 10, height: 10 },
    { kind: 'baking-dish', width: 7, height: 11 },
    { kind: 'baking-dish', width: 9, height: 13 },
  ],
  'sheet-pan': [
    { kind: 'sheet-pan', width: 6.5, height: 9.5 }, // eighth
    { kind: 'sheet-pan', width: 9.5, height: 13 }, // quarter
    { kind: 'sheet-pan', width: 13, height: 18 }, // half
    { kind: 'sheet-pan', width: 15, height: 21 }, // three-quarter
    { kind: 'sheet-pan', width: 18, height: 26 }, // full
  ],
  skillet: [
    { kind: 'skillet', diameter: 6 },
    { kind: 'skillet', diameter: 8 },
    { kind: 'skillet', diameter: 10 },
    { kind: 'skillet', diameter: 12 },
  ],
  pot: [
    { kind: 'pot', quarts: 1 },
    { kind: 'pot', quarts: 2 },
    { kind: 'pot', quarts: 3 },
    { kind: 'pot', quarts: 4 },
    { kind: 'pot', quarts: 6 },
    { kind: 'pot', quarts: 8 },
  ],
};

/** Key of the vessel a bare `{vessel}` resolves to, and the one `perVessel` scaling divides by. */
export const MAIN_VESSEL = 'main';

const POT_UNIT = 'quart';

const NAMES: Record<Vessel['kind'], { singular: string; plural: string }> = {
  'baking-dish': { singular: 'baking dish', plural: 'baking dishes' },
  'sheet-pan': { singular: 'sheet pan', plural: 'sheet pans' },
  skillet: { singular: 'skillet', plural: 'skillets' },
  pot: { singular: 'pot', plural: 'pots' },
};

const COUNT_WORDS = ['', '', 'two', 'three', 'four'];

// Capacity metric for choosing a size. Only ever compared within one kind, so
// the units need not agree across kinds.
const size = (v: Vessel): number => {
  if (v.kind === 'skillet') return Math.PI * (v.diameter / 2) ** 2;
  if (v.kind === 'pot') return v.quarts;
  return v.width * v.height;
};

export interface VesselSelection {
  vessel: Vessel;
  count: number;
}

export function selectVessel(base: Vessel, multiplier: number): VesselSelection {
  if (multiplier === 1) return { vessel: base, count: 1 };
  const target = size(base) * multiplier;
  let best = { vessel: base, count: 1, delta: Math.abs(size(base) - target) };
  for (const v of STANDARDS[base.kind]) {
    const delta = Math.abs(size(v) - target);
    if (delta < best.delta) best = { vessel: v, count: 1, delta };
  }
  for (let n = 2; n <= 4; n++) {
    const delta = Math.abs(size(base) * n - target);
    if (delta < best.delta) best = { vessel: base, count: n, delta };
  }
  return { vessel: best.vessel, count: best.count };
}

export function formatVessel({ vessel, count }: VesselSelection, system: UnitSystem): string {
  let dims: string;
  if (vessel.kind === 'skillet') {
    const d = convertUnit(vessel.diameter, 'in', system);
    dims = `${formatQty(d.qty, d.unit)}-${d.unit}`;
  } else if (vessel.kind === 'pot') {
    const c = convertUnit(vessel.quarts, POT_UNIT, system);
    dims = `${formatQty(c.qty, c.unit)}-${c.unit}`;
  } else {
    const shortIn = Math.min(vessel.width, vessel.height);
    const longIn = Math.max(vessel.width, vessel.height);
    const s = convertUnit(shortIn, 'in', system);
    const l = convertUnit(longIn, 'in', system);
    dims = `${formatQty(s.qty, s.unit)}×${formatQty(l.qty, l.unit)}-${s.unit}`;
  }
  const names = NAMES[vessel.kind];
  if (count === 1) return `${dims} ${names.singular}`;
  return `${COUNT_WORDS[count] ?? count} ${dims} ${names.plural}`;
}
